// Encrypted attachments ("hearth-att v1"). Shared by the page and the Service Worker.
//
// A file is split into fixed-size plaintext chunks, each sealed with AES-256-GCM under a
// random per-file key. Chunk i uses nonce = base XOR i (last 4 bytes, big-endian) and
// AAD = "hearth-att:v1" || u32(i) || u8(is_last), so chunks can't be reordered, swapped
// between files, or truncated. Fixed sizes make any plaintext byte range map to a
// ciphertext byte range — which is what makes seekable streaming possible.

export const CHUNK_SIZE = 1024 * 1024
export const TAG_SIZE = 16
const CONTEXT = new TextEncoder().encode('hearth-att:v1')

export interface MediaKeyInfo {
  key: string
  nonce: string
  size: number
  chunk: number
}

export function b64url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function fromB64url(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4))
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function chunkCount(size: number, chunk: number): number {
  return Math.max(1, Math.ceil(size / chunk))
}

export function ciphertextSize(size: number, chunk: number): number {
  return size + chunkCount(size, chunk) * TAG_SIZE
}

export function chunkNonce(base: Uint8Array, index: number): Uint8Array<ArrayBuffer> {
  const n = new Uint8Array(new ArrayBuffer(12))
  n.set(base)
  const view = new DataView(n.buffer)
  view.setUint32(8, view.getUint32(8) ^ index)
  return n
}

export function chunkAad(index: number, isLast: boolean): Uint8Array<ArrayBuffer> {
  const a = new Uint8Array(new ArrayBuffer(CONTEXT.length + 5))
  a.set(CONTEXT)
  new DataView(a.buffer).setUint32(CONTEXT.length, index)
  a[CONTEXT.length + 4] = isLast ? 1 : 0
  return a
}

async function importKey(raw: Uint8Array<ArrayBuffer>, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [usage])
}

export interface FileEncryptor {
  info: MediaKeyInfo
  /** Number of chunks. */
  chunks: number
  /** Ciphertext of chunk `i` (deterministic, so a resumed upload can re-encrypt it). */
  encryptChunk(i: number): Promise<ArrayBuffer>
}

/**
 * Fresh key material for `file`; chunks are encrypted on demand, never all at once. `same`
 * reuses a resumed upload's key material, which is only safe for the very same bytes
 * (the caller checks: a different file under the same nonces would leak both).
 */
export async function fileEncryptor(file: Blob, chunk = CHUNK_SIZE, same?: MediaKeyInfo): Promise<FileEncryptor> {
  if (same && same.size !== file.size) throw new Error('not the same file')
  if (same) chunk = same.chunk
  const keyBytes = same ? fromB64url(same.key) : crypto.getRandomValues(new Uint8Array(new ArrayBuffer(32)))
  const base = same ? fromB64url(same.nonce) : crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)))
  const key = await importKey(keyBytes, 'encrypt')
  const n = chunkCount(file.size, chunk)
  return {
    info: { key: b64url(keyBytes), nonce: b64url(base), size: file.size, chunk },
    chunks: n,
    async encryptChunk(i) {
      const plain = await file.slice(i * chunk, Math.min(file.size, (i + 1) * chunk)).arrayBuffer()
      return crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: chunkNonce(base, i), additionalData: chunkAad(i, i === n - 1) },
        key,
        plain,
      )
    },
  }
}

/** Encrypts a whole file in memory. Returns ciphertext and the key material to share. */
export async function encryptFile(
  file: Blob,
  chunk = CHUNK_SIZE,
  onProgress?: (fraction: number) => void,
): Promise<{ ciphertext: Blob; info: MediaKeyInfo }> {
  const enc = await fileEncryptor(file, chunk)
  const parts: ArrayBuffer[] = []
  for (let i = 0; i < enc.chunks; i++) {
    parts.push(await enc.encryptChunk(i))
    onProgress?.((i + 1) / enc.chunks)
  }
  return { ciphertext: new Blob(parts), info: enc.info }
}

/**
 * Encrypts and uploads `enc` in parts of `partChunks` chunks through a resumable upload,
 * holding at most one part in memory. A failed part is retried from wherever the server
 * says it got to (parts land atomically, so that is always a chunk boundary).
 */
export async function uploadResumable(
  api: {
    startUpload(size: number): Promise<{ id: string; received: number }>
    uploadPart(id: string, offset: number, part: Blob): Promise<{ received: number }>
    uploadStatus(id: string): Promise<{ received: number }>
    completeUpload(id: string): Promise<{ id: string; size: number }>
    cancelUpload(id: string): Promise<void>
  },
  enc: FileEncryptor,
  opts: { partChunks?: number; retries?: number; onProgress?: (fraction: number) => void; wait?: (ms: number) => Promise<void> } = {},
): Promise<{ id: string; size: number }> {
  const { chunk, size } = enc.info
  const total = ciphertextSize(size, chunk)
  const partChunks = opts.partChunks ?? 8
  const retries = opts.retries ?? 8
  const wait = opts.wait ?? ((ms) => new Promise<void>((r) => setTimeout(r, ms)))
  const up = await api.startUpload(total)
  let received = up.received
  let failures = 0
  while (received < total) {
    const first = received / (chunk + TAG_SIZE)
    if (!Number.isInteger(first)) throw new Error('upload out of step with the server')
    const parts: ArrayBuffer[] = []
    for (let i = first; i < Math.min(enc.chunks, first + partChunks); i++) parts.push(await enc.encryptChunk(i))
    try {
      received = (await api.uploadPart(up.id, received, new Blob(parts))).received
      failures = 0
    } catch (e) {
      // Limits and permissions won't fix themselves; network trouble might.
      const status = (e as { status?: number }).status ?? 0
      if (++failures > retries || (status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429)) {
        await api.cancelUpload(up.id).catch(() => undefined)
        throw e
      }
      await wait(Math.min(30_000, 500 * 2 ** failures))
      received = await api.uploadStatus(up.id).then((s) => s.received, () => received)
    }
    opts.onProgress?.(received / total)
  }
  return api.completeUpload(up.id)
}

/** Decrypts consecutive ciphertext chunks starting at chunk `first`. */
export async function decryptChunks(
  info: MediaKeyInfo,
  first: number,
  ciphertext: ArrayBuffer,
): Promise<Uint8Array<ArrayBuffer>> {
  const key = await importKey(fromB64url(info.key), 'decrypt')
  const base = fromB64url(info.nonce)
  const total = chunkCount(info.size, info.chunk)
  const ctChunk = info.chunk + TAG_SIZE
  const outParts: ArrayBuffer[] = []
  let off = 0
  let i = first
  while (off < ciphertext.byteLength) {
    const end = Math.min(ciphertext.byteLength, off + ctChunk)
    if (i >= total) throw new Error('ciphertext longer than expected')
    outParts.push(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: chunkNonce(base, i), additionalData: chunkAad(i, i === total - 1) },
        key,
        ciphertext.slice(off, end),
      ),
    )
    off = end
    i++
  }
  const len = outParts.reduce((n, p) => n + p.byteLength, 0)
  const out = new Uint8Array(new ArrayBuffer(len))
  let o = 0
  for (const p of outParts) {
    out.set(new Uint8Array(p), o)
    o += p.byteLength
  }
  return out
}

/** Decrypts a whole ciphertext; fails if it was truncated or tampered with. */
export async function decryptAll(info: MediaKeyInfo, ciphertext: ArrayBuffer): Promise<Uint8Array<ArrayBuffer>> {
  if (ciphertext.byteLength !== ciphertextSize(info.size, info.chunk)) {
    throw new Error('attachment has the wrong length (truncated or corrupted)')
  }
  return decryptChunks(info, 0, ciphertext)
}

/** Ciphertext byte range (inclusive) covering plaintext bytes [start, end]. */
export function ciphertextRange(
  info: MediaKeyInfo,
  start: number,
  end: number,
): { firstChunk: number; ctStart: number; ctEnd: number; skip: number } {
  const firstChunk = Math.floor(start / info.chunk)
  const lastChunk = Math.floor(end / info.chunk)
  const ctChunk = info.chunk + TAG_SIZE
  const ctStart = firstChunk * ctChunk
  const ctEnd = Math.min((lastChunk + 1) * ctChunk, ciphertextSize(info.size, info.chunk)) - 1
  return { firstChunk, ctStart, ctEnd, skip: start - firstChunk * info.chunk }
}

/** Parses a `Range: bytes=a-b` header against a file size (single range only). */
export function parseRange(header: string | null, size: number, maxSpan: number): { start: number; end: number } | null {
  if (!header) return null
  const m = /^bytes=(\d*)-(\d*)$/.exec(header.trim())
  if (!m) return null
  let start: number
  let end: number
  if (m[1] === '' && m[2] !== '') {
    // Suffix range: last N bytes.
    start = Math.max(0, size - Number(m[2]))
    end = size - 1
  } else {
    start = Number(m[1])
    end = m[2] === '' ? size - 1 : Math.min(Number(m[2]), size - 1)
  }
  if (Number.isNaN(start) || start >= size || end < start) return null
  // Open-ended requests get a bounded slice; players ask for more as needed.
  end = Math.min(end, start + maxSpan - 1)
  return { start, end }
}
